/**
 * The API client.
 *
 * The single seam between the app and the backend. Features call services,
 * services call this — nothing else issues HTTP. Wire types come from
 * `@shared/contracts`; this file is the only place that knows Laravel's
 * envelope shape, and it normalizes it so every hook above sees one contract.
 *
 * To use mock data, set VITE_USE_MOCK_DATA=true in .env
 */
import type {
  ApiResponse,
  PaginatedQueryParams,
  PaginatedResponse,
  PaginationType,
} from "@shared/contracts";
import { isLaravelEnvelope, toOffsetMeta } from "@shared/contracts";
import { env } from "@shared/config";
import { safeJsonParse } from "../helpers";
import { onApiError, onApiRequest, onUnauthorized } from "../interceptors";
import type { ApiMethod, HttpClient, QueryParams } from "@shared/api";
import { mockApi } from "../mocks";
import { ApiError } from "./api-error";
import { getAccessToken } from "./session-bridge";
import { refreshSession, shouldAttemptRefresh } from "./refresh-token";

interface RawResponse {
  parsed: unknown;
  status: number;
}

interface RequestSpec {
  method: ApiMethod;
  path: string;
  query?: QueryParams;
  body?: unknown;
}

class ApiClient implements HttpClient {
  private readonly baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/+$/, "");
  }

  /**
   * CAUTION: `Accept` must be exactly `application/json`.
   */
  private headers(isForm: boolean): Record<string, string> {
    const headers: Record<string, string> = {
      Accept: "application/json",
      "Accept-Language": env.api.locale,
    };

    // FormData sets its own Content-Type, including the multipart boundary.
    if (!isForm) {
      headers["Content-Type"] = "application/json";
    }

    const token = getAccessToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * `new URL(path, base)` would silently discard the base's own path whenever
   * `path` starts with a slash — `new URL('/games', 'http://x/api')` yields
   * `http://x/games`. Concatenate instead.
   */
  private resolve(path: string, query?: QueryParams): string {
    const suffix = path.startsWith("/") ? path : `/${path}`;
    const base = `${this.baseUrl}${suffix}`;

    if (!query) return base;

    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) {
        search.set(key, String(value));
      }
    }

    const queryString = search.toString();
    return queryString ? `${base}?${queryString}` : base;
  }

  private send(spec: RequestSpec): Promise<Response> {
    const isForm = spec.body instanceof FormData;

    return fetch(this.resolve(spec.path, spec.query), {
      method: spec.method,
      headers: this.headers(isForm),
      body:
        spec.body === undefined
          ? undefined
          : isForm
            ? (spec.body as FormData)
            : JSON.stringify(spec.body),
    });
  }

  private async execute(
    spec: RequestSpec,
    isRetry = false
  ): Promise<RawResponse> {
    onApiRequest(spec.method, spec.path);

    let response: Response;
    try {
      response = await this.send(spec);
    } catch (cause) {
      // Only genuine transport failures land here. Keeping the non-2xx branch
      // below outside this try is what stops real HTTP errors being rewritten
      // as network errors.
      const error = ApiError.network(spec.path, cause);
      onApiError(error, spec.path);
      throw error;
    }

    // 204 and other empty bodies would throw on a bare `.json()`.
    const text = await response.text();
    const parsed = text.length > 0 ? safeJsonParse(text) : undefined;

    if (!response.ok) {
      const error = ApiError.fromResponse(
        response.status,
        parsed,
        spec.path,
        text
      );

      if (error.kind === "UNAUTHORIZED") {
        // One renewal, one replay. `isRetry` makes a loop impossible: a 401 on
        // the replayed request falls straight through to the handler. FormData
        // bodies replay fine because we hold the object, not a consumed stream.
        if (!isRetry && shouldAttemptRefresh(spec.path)) {
          const renewed = await refreshSession();
          if (renewed) {
            return this.execute(spec, true);
          }
        }
        onUnauthorized();
      }

      onApiError(error, spec.path);
      throw error;
    }

    return { parsed, status: response.status };
  }

  /**
   * The only caller of `execute`: every verb goes through here so the envelope
   * normalization lives in exactly one place.
   */
  private async request<T>(spec: RequestSpec): Promise<ApiResponse<T>> {
    const { parsed, status } = await this.execute(spec);

    // No body at all (204, or a handler that returned early). Laravel's own
    // destroy returns a full envelope, so this is defensive.
    if (parsed === undefined) {
      return { statusCode: status, message: "", data: null as T };
    }

    if (!isLaravelEnvelope<T>(parsed)) {
      throw ApiError.malformed(
        spec.path,
        status,
        "response was not the Laravel envelope"
      );
    }

    // `data` is legitimately null on logout, reset-password and every
    // `noData()` branch, so the cast is confined here rather than pushed onto
    // callers.
    return {
      statusCode: status,
      message: parsed.message,
      data: parsed.data as T,
    };
  }

  async get<T>(path: string, query?: QueryParams): Promise<ApiResponse<T>> {
    return this.request<T>({ method: "GET", path, query });
  }

  async getPaginated<T, TPaginationType extends PaginationType = "offset">(
    path: string,
    query?: PaginatedQueryParams<TPaginationType> & QueryParams
  ): Promise<ApiResponse<PaginatedResponse<T, TPaginationType>>> {
    const { parsed, status } = await this.execute({
      method: "GET",
      path,
      query: query as QueryParams,
    });

    if (!isLaravelEnvelope(parsed)) {
      throw ApiError.malformed(
        path,
        status,
        "response was not the Laravel envelope"
      );
    }

    if (!Array.isArray(parsed.data)) {
      throw ApiError.malformed(
        path,
        status,
        "paginated response data was not an array"
      );
    }

    const items = parsed.data as T[];

    return {
      statusCode: status,
      message: parsed.message,
      data: {
        items,
        meta: toOffsetMeta(parsed.pagination_data, items.length, query),
      } as PaginatedResponse<T, TPaginationType>,
    };
  }

  async post<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>({ method: "POST", path, body });
  }

  /** Multipart upload — body goes as FormData, not JSON. */
  async upload<T>(path: string, formData: FormData): Promise<ApiResponse<T>> {
    return this.request<T>({ method: "POST", path, body: formData });
  }

  async put<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>({ method: "PUT", path, body });
  }

  async patch<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>({ method: "PATCH", path, body });
  }

  async delete<T = null>(path: string): Promise<ApiResponse<T>> {
    return this.request<T>({ method: "DELETE", path });
  }
}

/**
 * The `: HttpClient` annotation documents the contract but does not enforce the
 * envelope return (see the CAUTION in `http-client.interface.ts`) — the
 * provider tests do.
 */
export const api: HttpClient = env.api.useMockData
  ? mockApi
  : new ApiClient(env.api.url);
